import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { FormButton } from "../../components/FormButton";
import { InputText } from "../../components/InputText";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";

const schema = Yup.object().shape({
  nova_senha: Yup.string()
    .required("Nova senha é obrigatória")
    .min(8, "A nova senha deve ter pelo menos 8 caracteres"),
  confirmar_senha: Yup.string()
    .required("Confirmação de nova senha é obrigatória")
    .oneOf([Yup.ref("nova_senha"), null], "As senhas não correspondem"),
});

type Tab = "perfil" | "seguranca" | "integracoes";

export function MeuUsuario() {
  const { enqueueSnackbar } = useSnackbar();
  const { militar } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("perfil");

  const [banner, setBanner] = useState<string>("");
  const [bannerPositionY, setBannerPositionY] = useState<number>(50);
  const [savingBanner, setSavingBanner] = useState(false);

  const [bio, setBio] = useState<string>("");
  const [savingBio, setSavingBio] = useState(false);

  const [dataNascimento, setDataNascimento] = useState<string>("");
  const [savingDataNascimento, setSavingDataNascimento] = useState(false);

  const [discordId, setDiscordId] = useState<string>("");
  const [discordUsername, setDiscordUsername] = useState<string>("");
  const [savingDiscord, setSavingDiscord] = useState(false);

  useEffect(() => {
    api.get("/militares/me").then(({ data }) => {
      if (data.bio) setBio(data.bio);
      if (data.banner) setBanner(data.banner);
      if (data.banner_position != null) setBannerPositionY(data.banner_position);
      if (data.data_nascimento) setDataNascimento(data.data_nascimento);
      if (data.id_usuario_discord) setDiscordId(data.id_usuario_discord);
      if (data.discord_username) setDiscordUsername(data.discord_username);
    }).catch(() => {});
  }, []);

  // Captura o code do Discord OAuth2 retornado via query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) return;

    // Limpar o code da URL
    window.history.replaceState({}, "", window.location.pathname);
    setActiveTab("integracoes");
    setSavingDiscord(true);

    api.post("/militaresAcessos/discord/callback", { code })
      .then(({ data }) => {
        setDiscordId(data.id_usuario_discord);
        setDiscordUsername(data.discord_username);
        enqueueSnackbar("Discord vinculado com sucesso!", { variant: "success" });
      })
      .catch((err: any) => {
        enqueueSnackbar(err.response?.data?.message || "Erro ao vincular Discord", { variant: "error" });
      })
      .finally(() => setSavingDiscord(false));
  }, []);

  async function handleVincularDiscord() {
    try {
      const { data } = await api.get("/militaresAcessos/discord/auth");
      window.location.href = data.url;
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao iniciar vinculação", { variant: "error" });
    }
  }

  async function handleDesvincularDiscord() {
    setSavingDiscord(true);
    try {
      await api.patch("/militaresAcessos/discord/desvincular");
      setDiscordId("");
      setDiscordUsername("");
      enqueueSnackbar("Discord desvinculado com sucesso!", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao desvincular Discord", { variant: "error" });
    } finally {
      setSavingDiscord(false);
    }
  }

  function handleSubmit({ senha, nova_senha }) {
    api
      .post("/militaresAcessos/novaSenha", {
        id_militar: militar.id,
        senha,
        nova_senha,
      })
      .then(() => {
        enqueueSnackbar("Senha alterada com sucesso!", { variant: "success" });
      })
      .catch(({ response }) => {
        enqueueSnackbar(response.data.message, { variant: "warning" });
      });
  }

  const initialValues = { senha: "", nova_senha: "", confirmar_senha: "" };

  async function handleSalvarBanner(e: React.FormEvent) {
    e.preventDefault();
    setSavingBanner(true);
    try {
      await api.patch("/militares/banner", { banner: banner.trim() || null, banner_position: bannerPositionY });
      enqueueSnackbar("Banner atualizado com sucesso!", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar banner", { variant: "error" });
    } finally {
      setSavingBanner(false);
    }
  }

  async function handleSalvarBio(e: React.FormEvent) {
    e.preventDefault();
    setSavingBio(true);
    try {
      await api.patch("/militares/bio", { bio: bio.trim() || null });
      enqueueSnackbar("Biografia atualizada com sucesso!", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar biografia", { variant: "error" });
    } finally {
      setSavingBio(false);
    }
  }

  async function handleSalvarDataNascimento(e: React.FormEvent) {
    e.preventDefault();
    setSavingDataNascimento(true);
    try {
      await api.patch("/militares/data-nascimento", {
        data_nascimento: dataNascimento || null,
      });
      enqueueSnackbar("Data de nascimento salva com sucesso!", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar data de nascimento", { variant: "error" });
    } finally {
      setSavingDataNascimento(false);
    }
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    {
      id: "perfil",
      label: "Meu Perfil",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      id: "seguranca",
      label: "Segurança",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      id: "integracoes",
      label: "Integrações",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8">
        <div className="flex items-center space-x-6">
          <div className="relative">
            <img
              className="h-24 w-24 rounded-2xl object-cover ring-4 ring-foreground/20 dark:ring-white/30"
              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${militar.nick}&direction=3&head_direction=2&size=l&headonly=1`}
              alt={militar.nick}
            />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-accent rounded-full border-4 border-background flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1 text-foreground dark:text-white">{militar.nick}</h1>
            <p className="text-foreground/70 dark:text-white/80 text-lg">Painel Exército Brasileiro</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        {/* Tab bar */}
        <div className="flex border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px
                ${activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Meu Perfil */}
        {activeTab === "perfil" && (
          <div className="divide-y divide-border">
            {/* Biografia */}
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Biografia
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Escreva um texto sobre você que aparecerá no seu perfil público
                </p>
              </div>
              <form onSubmit={handleSalvarBio} className="space-y-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-foreground">Sobre você</label>
                  <textarea
                    rows={5}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Conte um pouco sobre você, sua trajetória no exército, seus objetivos..."
                    maxLength={1000}
                    className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  />
                  <p className="text-xs text-muted-foreground text-right">{bio.length}/1000 caracteres</p>
                </div>
                <div className="flex justify-end">
                  <FormButton type="submit" title="Salvar Biografia" loading={savingBio} size="lg" />
                </div>
              </form>
            </div>

            {/* Data de Nascimento */}
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                  </svg>
                  Data de Nascimento
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Utilizada para a nossa lista de aniversariantes
                </p>
              </div>
              <form onSubmit={handleSalvarDataNascimento} className="space-y-4">
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-foreground mb-1">Data</label>
                  <input
                    type="date"
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="flex justify-end">
                  <FormButton type="submit" title="Salvar Data" loading={savingDataNascimento} size="lg" />
                </div>
              </form>
            </div>

            {/* Banner do Perfil — exclusivo para patrocinadores */}
            {militar.patrocinador && (
              <div className="p-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Banner do Perfil
                    <span className="ml-2 text-xs font-medium bg-accent/20 text-accent border border-accent/30 px-2 py-0.5 rounded-full">
                      Patrocinador
                    </span>
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Personalize seu perfil público com um banner — dimensões recomendadas:{" "}
                    <span className="font-semibold text-foreground">1860 × 270 px</span>
                  </p>
                </div>
                {banner && (
                  <div className="mb-4 space-y-3">
                    <div className="w-full rounded-xl overflow-hidden border border-border" style={{ aspectRatio: "1860/270" }}>
                      <img
                        src={banner}
                        alt="Preview do banner"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `center ${bannerPositionY}%` }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-muted-foreground">Posição vertical da imagem</label>
                        <span className="text-xs text-muted-foreground">{bannerPositionY}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={bannerPositionY}
                        onChange={(e) => setBannerPositionY(Number(e.target.value))}
                        className="w-full h-2 rounded-full accent-primary cursor-pointer bg-border"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Topo</span>
                        <span>Centro</span>
                        <span>Base</span>
                      </div>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSalvarBanner} className="space-y-4">
                  <InputText
                    name="banner"
                    title="URL da imagem do banner"
                    placeholder="https://exemplo.com/meu-banner.png  (1860 × 270 px)"
                    value={banner}
                    onChange={(e) => setBanner(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <FormButton type="submit" title="Salvar Banner" loading={savingBanner} size="lg" />
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Tab: Segurança */}
        {activeTab === "seguranca" && (
          <div className="divide-y divide-border">
            {/* Alterar Senha */}
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Alterar Senha
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Altere sua senha para manter sua conta segura
                </p>
              </div>
              <Formik
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
              >
                {({ errors, isSubmitting, getFieldProps }) => (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="lg:col-span-2">
                        <InputText
                          type="password"
                          title="Senha Atual"
                          placeholder="Digite sua senha atual"
                          error={errors.senha}
                          showToggle
                          {...getFieldProps("senha")}
                        />
                      </div>
                      <InputText
                        type="password"
                        title="Nova Senha"
                        placeholder="Digite sua nova senha"
                        error={errors.nova_senha}
                        showToggle
                        {...getFieldProps("nova_senha")}
                      />
                      <InputText
                        type="password"
                        title="Confirmar Nova Senha"
                        placeholder="Confirme sua nova senha"
                        error={errors.confirmar_senha}
                        showToggle
                        {...getFieldProps("confirmar_senha")}
                      />
                    </div>
                    <div className="flex justify-end">
                      <FormButton type="submit" title="Alterar Senha" loading={isSubmitting} size="lg" />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>

            {/* Dicas de Segurança */}
            <div className="p-8">
              <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Dicas de Segurança
              </h3>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                {[
                  "Use uma senha com pelo menos 8 caracteres",
                  "Combine letras maiúsculas, minúsculas, números e símbolos",
                  "Não compartilhe sua senha com outras pessoas",
                  "Altere sua senha regularmente",
                ].map((dica) => (
                  <li key={dica} className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {dica}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Tab: Integrações */}
        {activeTab === "integracoes" && (
          <div className="divide-y divide-border">
            {/* Discord */}
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                  </svg>
                  Integração com Discord
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Vincule sua conta do Discord para utilizar funcionalidades integradas ao servidor
                </p>
              </div>

              {discordId ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border bg-muted/40 p-5 flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#5865F2]/10">
                      <svg className="w-6 h-6 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Conta vinculada{discordUsername ? `: ${discordUsername}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        ID: {discordId}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        Vinculado
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleDesvincularDiscord}
                    disabled={savingDiscord}
                    className="text-sm text-destructive hover:text-destructive/80 transition-colors border border-destructive/40 dark:border-destructive/60 rounded-lg px-4 py-2 disabled:opacity-50"
                  >
                    {savingDiscord ? "Desvinculando..." : "Desvincular Discord"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground space-y-2">
                    <p className="font-medium text-foreground">Como funciona:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Clique no botão abaixo para ser redirecionado ao Discord</li>
                      <li>Autorize o acesso à sua conta (apenas leitura do seu perfil)</li>
                      <li>Sua conta será vinculada automaticamente ao retornar</li>
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={handleVincularDiscord}
                    disabled={savingDiscord}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "#5865F2" }}
                  >
                    {savingDiscord ? (
                      "Vinculando..."
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                        </svg>
                        Vincular com Discord
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
